"use client";

import type {ApprovalDecisionOutcome} from "@/types/chatMessages";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";
import UseCaseSnapshotDiff from "./UseCaseSnapshotDiff";
import ScriptSnapshotDiff from "./ScriptSnapshotDiff";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";
import {presentationText, type ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";
import {displayToolPresentation} from "@repo/ui/gailgpt/tool-display";
import {approvalPresentation, approvalStatusKey} from "./approvalPresentation";
import ToolPresentationDetails from "./ToolPresentationDetails";

interface ToolApprovalCardProps {
   embedded?: boolean;
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}

function approvalCardCopy(call: MessageContentFunctionCall, presentation: ToolPresentation | undefined, t: TFunction<"ask">) {
   const structured = !call.approval?.proposal && !call.approval?.previewUnavailable;
   if (structured) return {
      structured,
      title: presentation ? presentationText(presentation.title, t) : call.functionName,
      approveLabel: presentation?.approveLabel ? presentationText(presentation.approveLabel, t) : t("toolApproval.execution.approve"),
      denyLabel: t("toolApproval.execution.cancel"),
      description: presentation?.description ? presentationText(presentation.description, t) : t("toolApproval.execution.description"),
   };
   return {
      structured,
      title: call.approval?.proposal?.summary ?? call.functionName,
      approveLabel: t("toolApproval.apply"),
      denyLabel: t("toolApproval.discard"),
      description: t("toolApproval.previewDescription"),
   };
}

function ToolApprovalActions({decision, copy, unavailable}: {
   decision: ReturnType<typeof useToolApprovalDecision>;
   copy: ReturnType<typeof approvalCardCopy>;
   unavailable: boolean;
}) {
   const {queued, canRespond, canApply, respond} = decision;
   return <div className={`border-t border-neutral bg-panel pb-4 pt-1 ${copy.structured ? "px-5" : "sticky bottom-0 px-4"}`}>
      {!unavailable && <p className="mt-3 text-xs leading-relaxed text-500">{copy.description}</p>}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
         <Button type="tertiary" compact disabled={!canRespond} aria-pressed={queued === false} onClick={() => void respond(false)}
            className="aria-pressed:border-800 aria-pressed:bg-neutral/40"
            iconBefore={queued === false ? <Icon icon="check" className="h-3 w-3" /> : undefined}>
            {copy.denyLabel}
         </Button>
         <Button type="primary" compact disabled={!canApply} aria-pressed={queued === true} onClick={() => void respond(true)}
            className="aria-pressed:ring-2 aria-pressed:ring-primary aria-pressed:ring-offset-2"
            iconBefore={queued === true ? <Icon icon="check" className="h-3 w-3" /> : undefined}>
            {copy.approveLabel}
         </Button>
      </div>
   </div>;
}

export default function ToolApprovalCard({embedded = false, call, completed, result, active, disabled, onRespond, onRequestFresh}: ToolApprovalCardProps) {
   const {t} = useTranslation("ask");
   const decision = useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh});
   const {status, settled, expired, error, ownerOnly, saved, queued, canRequestFresh, requestFresh, requestedFresh} = decision;
   const approval = call.approval;
   if (!approval) return null;

   const {presentation: validated, unavailable} = approvalPresentation(call);
   const presentation = validated ? displayToolPresentation(validated, call.parameters, t) : undefined;
   const copy = approvalCardCopy(call, presentation, t);
   const {structured, title} = copy;
   const statusKey = approvalStatusKey(call, status);
   const heading = structured ? <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h3 className="min-w-0 break-words text-sm font-semibold leading-relaxed text-900">{title}</h3>
      <output className="text-xs font-medium text-500">{t(statusKey)}</output>
   </div> : <>
      <output className="block text-xs font-medium uppercase tracking-wider text-500">{t(statusKey)}</output>
      <span className="mt-1 block break-words text-sm font-semibold text-800">{title}</span>
   </>;

   return (
      <section aria-label={t(structured ? "toolApproval.execution.title" : "toolApproval.title")}
         className={`${embedded ? "overflow-clip border-t border-neutral" : "my-3 overflow-clip rounded-xl border border-neutral bg-panel"} ${structured ? "max-w-xl" : ""}`}>
         {(structured || !embedded) && <div className={structured ? "px-5 py-4" : "border-b border-neutral px-4 py-3"}>{heading}</div>}
         {structured
            ? !unavailable && presentation && <ToolPresentationDetails presentation={presentation} parameters={call.parameters} />
            : <ToolApprovalContent call={call} saved={saved} />}
         <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error} ownerOnly={ownerOnly} canRequestFresh={canRequestFresh} onRequestFresh={requestFresh} requestedFresh={requestedFresh}
            previewUnavailable={unavailable} queued={queued} operation={structured ? "execution" : undefined} className="mx-4 my-3 text-sm" />
         {!settled && active && <ToolApprovalActions decision={decision} copy={copy} unavailable={unavailable} />}
      </section>
   );
}

function ToolApprovalContent({call, saved}: {call: MessageContentFunctionCall; saved?: Record<string, unknown> | null}) {
   const approval = call.approval;
   if (!approval) return null;
   if (approval.proposal) {
      if (approval.proposal.capability === "use_cases_update") return <UseCaseSnapshotDiff preview={approval.proposal} saved={saved} />;
      return <ScriptSnapshotDiff preview={approval.proposal} />;
   }
   return null;
}
